import { useEffect, useRef } from "react"
import { observer } from "mobx-react"
import React, { useState } from "react"
import AppRequests from "components/ProjectSpecificComponents/API/AppRequests/AppRequests"
import { IPOST_RESPONSE_Logo } from "components/ProjectSpecificComponents/Interfaces/DataInterface"
import HyperDiv from "components/ProjectNonSpecificComponents/Elements/HyperDiv/HyperDiv"
import Whitespace from "components/ProjectNonSpecificComponents/Elements/Whitespace"
import { useTranslation } from "react-i18next"
import { useFormStore } from "components/ProjectSpecificComponents/Contexts/FormStoreContext"
import { useStore } from "components/ProjectSpecificComponents/Contexts/GlobalStoreContext"
import HorizontalFormField from "../HorizontalFormField"
import InputWithHashtagIndicationAndColorBox from "components/ProjectNonSpecificComponents/Elements/ColorPicker/InputWithHashtagIndicationAndColorBox"
import {
	isReactSelectSingleOption,
	TReactSelectOption,
	TReactSelectSelectedOptions,
} from "components/ProjectSpecificComponents/Interfaces/ExternalComponentsInterfaces"
import Select from "components/ProjectSpecificComponents/Elements/Global/atoms/Select"
import PreferencesSmallButton from "components/ProjectSpecificComponents/Elements/Global/atoms/Buttons/PreferencesSmallButton"
import {
	BLUE_COLOR_THEME,
	GREEN_COLOR_THEME,
	ORANGE_COLOR_THEME,
	RED_COLOR_THEME,
} from "components/ProjectSpecificComponents/Constants/COLOR_THEMES"
import { IColor } from "components/ProjectSpecificComponents/Interfaces/FormsInterface"
import UploadButton from "components/ProjectNonSpecificComponents/Elements/UploadButton/UploadButton"
import Icon from "components/ProjectSpecificComponents/Elements/Global/atoms/Icons/Icon/Icon"
import Checkbox from "components/ProjectSpecificComponents/Elements/Global/atoms/Checkbox"

interface Props {}

interface ILogo {
	size: "s" | "l"
	Logo: FormData
}

export const PersonalizeForm = observer(({}: Props) => {
	const [image, setImage] = useState<FormData>()
	const [imgName, setImgName] = useState<string>("")
	const [bigIcon, setBigIcon] = useState<boolean>(false)

	const store = useStore()
	const { t } = useTranslation()
	const formStore = useFormStore()

	const onChangeFile = async (files: any) => {
		/** Only single file available */
		const file: File = files[0]
		/** Temp upload to view before DB save */
		const tmpPath = URL.createObjectURL(file)
		setImgName(file.name)
		store.setTempIconUrl(tmpPath)
		/** For saving in DB */
		const formData = new FormData()
		formData.append("Logo", file)
		setImage(formData)
	}

	const onSavePersonalize = async () => {
		saveImage()
		const { ok, data } = await AppRequests("Setting").post<IColor>(
			"save-personalize",
			formStore.settingsData.personalize
		)

		if (ok) {
			store.setInfoModalConfig({
				type: "info",
				titleKey: "data-saving.success",
				showDuration: 1000,
			})
			store.setShowAdminSettingsModal(false)
		} else {
			store.setInfoModalConfig({
				type: "fail",
				titleKey: "data-saving.failed",
			})
		}
	}
	const saveImage = async () => {
		if (!image) return

		const { ok, data } = await AppRequests("Setting").post<FormData, IPOST_RESPONSE_Logo>(
			"save-logo",
			image
		)

		if (ok && data) {
			store.setInfoModalConfig({
				titleKey: "file.success.upload",
				type: "info",
				showDuration: 5000,
			})
			store.triggerLogoUploaded()
			store.setTempIconUrl("")
			setImgName("")
		} else {
			store.setInfoModalConfig({
				titleKey: "file.fail.upload",
				type: "fail",
			})
		}
	}

	useEffect(() => {
		return () => {
			store.setTempIconUrl("")
		}
	}, [])

	return (
		<HyperDiv direction="column" padding=".5rem 0" spaceBetweenElems="2.25rem">
			{/* <HorizontalFormField
				containerStyle={{ padding: "8px 16px" }}
				label={t(`forms.field-label.color-theme`)}
				centerSpaceMultiplier={0.5}
				tooltipBelowInputField
			>
				<Whitespace h x="1" />
				<Select options={colorOptions} onChange={onColorThemeChange} />
			</HorizontalFormField> */}
			<HorizontalFormField
				containerStyle={{ padding: "8px 16px" }}
				label={t(`forms.field-label.header-color`)}
				centerSpaceMultiplier={0.5}
				tooltipBelowInputField
			>
				<InputWithHashtagIndicationAndColorBox
					onChangeText={(color) => {
						formStore.mergePersonalizationFormData({ colorHeader: color })
					}}
					defaultValue={formStore.settingsData.personalize.colorHeader}
					maxLength={8}
				/>
			</HorizontalFormField>
			<HorizontalFormField
				containerStyle={{ padding: "8px 16px" }}
				label={t(`forms.field-label.header-text`)}
				centerSpaceMultiplier={0.5}
				tooltipBelowInputField
			>
				<InputWithHashtagIndicationAndColorBox
					onChangeText={(color) => {
						formStore.mergePersonalizationFormData({ colorHeaderText: color })
					}}
					defaultValue={formStore.settingsData.personalize.colorHeaderText}
					maxLength={8}
				/>
			</HorizontalFormField>
			<HorizontalFormField
				containerStyle={{ padding: "8px 16px" }}
				label={t(`forms.field-label.button-color`)}
				centerSpaceMultiplier={0.5}
				tooltipBelowInputField
			>
				<InputWithHashtagIndicationAndColorBox
					onChangeText={(color) => {
						formStore.mergePersonalizationFormData({ colorButton: color })
					}}
					defaultValue={formStore.settingsData.personalize.colorButton}
					maxLength={8}
				/>
			</HorizontalFormField>
			<HorizontalFormField
				containerStyle={{ padding: "8px 16px" }}
				label={t(`forms.field-label.button-text`)}
				centerSpaceMultiplier={0.5}
				tooltipBelowInputField
			>
				<InputWithHashtagIndicationAndColorBox
					onChangeText={(color) => {
						formStore.mergePersonalizationFormData({ colorButtonText: color })
					}}
					defaultValue={formStore.settingsData.personalize.colorButtonText}
					maxLength={8}
				/>
			</HorizontalFormField>
			<HorizontalFormField
				containerStyle={{ padding: "8px 16px 0px 8px" }}
				label={t(`rawText.header-logo`)}
				centerSpaceMultiplier={0.5}
				tooltipBelowInputField
			>
				<HyperDiv
					style={{
						height: 38,
						border: "1px solid var(--gray-c2)",
						borderRadius: 5,
					}}
					centered
				>
					<UploadButton
						disableMultipleFiles
						containerStyle={{ width: "100%", height: "100%" }}
						onUpdateFiles={(file) => onChangeFile(file)}
					>
						<HyperDiv
							className="fill-cter"
							style={{
								width: "100%",
								color: formStore.settingsData.personalize.colorButtonText ?? "var(--blue)",
								background: formStore.settingsData.personalize.colorButton,
							}}
							centered
							direction="row"
							spaceBetweenElems=".75rem"
						>
							{t("rawText.upload")}
							<Icon
								iconKey="upload"
								iconColor={formStore.settingsData.personalize.colorButtonText ?? "var(--blue)"}
							/>
						</HyperDiv>
					</UploadButton>
				</HyperDiv>
			</HorizontalFormField>
			<HorizontalFormField
				containerStyle={{ padding: "8px 16px" }}
				label={t(`rawText.big-icon`)}
				centerSpaceMultiplier={0.5}
				tooltipBelowInputField
			>
				<Checkbox
					checked={formStore.settingsData.personalize.bigLogo}
					onCheck={(checked) => {
						formStore._mergeSettingsData("personalize", { bigLogo: !!checked })
					}}
				/>
			</HorizontalFormField>
			{store.tempIconUrl && (
				<div className="bg-gray-100 rounded mr-4">
					<HorizontalFormField
						containerStyle={{ padding: "8px 16px" }}
						label={t(`forms.field-label.name`)}
						centerSpaceMultiplier={0.5}
						tooltipBelowInputField
					>
						<HyperDiv centered>
							<label className=" text-sm">{imgName}</label>
						</HyperDiv>
					</HorizontalFormField>
				</div>
			)}
			<HyperDiv centeredX>
				<Whitespace h x="1" />
				<PreferencesSmallButton
					style={{
						width: "25%",
					}}
					onClick={onSavePersonalize}
					children={t("rawText.save")}
				/>
			</HyperDiv>
		</HyperDiv>
	)
})

export default PersonalizeForm
